import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { RelayProvider } from '@opengsn/provider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getAccount, getClient, getConnectorClient, switchChain } from '@wagmi/core';
import { clientToProvider, clientToSigner, gsnRawProvider } from 'context/Web3Provider/helpers/providers';
import { providers } from 'ethers';
import { motion } from 'framer-motion';
import {
  type Account,
  type Chain,
  type Client,
  type Transport,
  createPublicClient,
} from 'viem';
import {
  Connector,
  createConfig,
  http,
  useAccount,
  useClient,
  useConnect,
  useConnectorClient,
  UseConnectReturnType,
  useDisconnect,
  WagmiProvider
} from 'wagmi';
import { coinbaseWallet, injected, metaMask, walletConnect } from 'wagmi/connectors';

import { Wrap } from 'components/Web3Initializer';

import { chainIdToNetworkMap, gsnConfigMap, networkConfigsMap, ORIGIN_NETWORK_NAME } from 'constants/config';

const queryClient = new QueryClient();

export const qTestnet: Chain = {
  id: networkConfigsMap.testnet.chainId,
  name: networkConfigsMap.testnet.name,
  nativeCurrency: { name: networkConfigsMap.testnet.networkName, symbol: 'QGOV', decimals: 18 },
  rpcUrls: {
    default: {
      http: [networkConfigsMap.testnet.rpcUrl],
    },
  },
  blockExplorers: {
    default: {
      name: 'Q explorer',
      url: networkConfigsMap.testnet.explorerUrl,
      apiUrl: networkConfigsMap.testnet.explorerApiUrl,
    },
  },
  testnet: true,
};

export const qDevnet: Chain = {
  id: networkConfigsMap.devnet.chainId,
  name: networkConfigsMap.devnet.name,
  nativeCurrency: { name: networkConfigsMap.devnet.networkName, symbol: 'QGOV', decimals: 18 },
  rpcUrls: {
    default: {
      http: [networkConfigsMap.devnet.rpcUrl],
    },
  },
  blockExplorers: {
    default: {
      name: 'Q explorer',
      url: networkConfigsMap.devnet.explorerUrl,
      apiUrl: networkConfigsMap.devnet.explorerApiUrl,
    },
  },
  testnet: true,
};

export const qMainnet: Chain = {
  id: networkConfigsMap.mainnet.chainId,
  name: networkConfigsMap.mainnet.name,
  nativeCurrency: { name: networkConfigsMap.mainnet.networkName, symbol: 'QGOV', decimals: 18 },
  rpcUrls: {
    default: {
      http: [networkConfigsMap.mainnet.rpcUrl],
    },
  },
  blockExplorers: {
    default: {
      name: 'Q explorer',
      url: networkConfigsMap.mainnet.explorerUrl,
      apiUrl: networkConfigsMap.mainnet.explorerApiUrl,
    },
  },
  sourceId: networkConfigsMap.mainnet.chainId,
};

export const config = createConfig({
  chains: [qTestnet, qDevnet, qMainnet],
  connectors: [
    injected(),
    metaMask({
      dappMetadata: {
        name: 'Q DAO Factory',
        url: window.location.origin,
        iconUrl: 'https://q.org/favicon/favicon.ico',
      }
    }),
    walletConnect({
      projectId: 'bcc3b8c2ab7a3bae68bd0205b6279806',
      metadata: {
        name: 'Q DAO Factory',
        description: 'Q DAO Factory App',
        url: window.location.origin,
        // TODO: Change for infura or s3
        icons: ['https://q.org/favicon/favicon.ico']
      },
      isNewChainsStale: true,
      qrModalOptions: {
        themeMode: 'dark',
        explorerRecommendedWalletIds: [
          'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // Metamask Wallet
          '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // Trust Wallet
          'c03dfee351b6fcc421b4494ea33b9d4b92a984f87aa76d1663bb28705e95034a', // Uniswap Wallet
          '1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369', // Rainbow Wallet
          '18388be9ac2d02726dbac9777c96efaac06d744b2f6d580fccdd4127a6d01fd1', // Rabby Wallet
          'c286eebc742a537cd1d6818363e9dc53b21759a1e8e5d9b263d0c03ec7703576', // 1inch Wallet
        ],
      }
    }),
    coinbaseWallet({
      appName: 'Q DAO Factory',
      version: '3',
      darkMode: true,
      // TODO: Change for infura or s3
      appLogoUrl: 'https://q.org/favicon/favicon.ico'
    })
  ],
  transports: {
    [qTestnet.id]: http(networkConfigsMap.testnet.rpcUrl),
    [qDevnet.id]: http(networkConfigsMap.devnet.rpcUrl),
    [qMainnet.id]: http(networkConfigsMap.mainnet.rpcUrl),
  },
});

const fallbackClient = createPublicClient({
  chain: qTestnet,
  transport: http(),
});

export type Web3ProviderContext<
  A extends Account | undefined = Account | undefined,
> = {
  connectManager: UseConnectReturnType;

  client: Client<Transport, Chain, A>;

  address: `0x${string}` | '';
  chain: Chain;
  isConnected: boolean;

  isRightNetwork: boolean;
  provider?: providers.JsonRpcProvider | providers.FallbackProvider;
  signer?: providers.JsonRpcSigner;
  connect: (connector: Connector) => void;
  disconnect: () => void;

  gsnSigner?: providers.JsonRpcSigner;

  safeSwitchChain: (id: number) => Promise<void>;
};

const web3ProviderContext = createContext<Web3ProviderContext>({
  connectManager: {} as UseConnectReturnType,
  client: fallbackClient,

  address: '',
  chain: fallbackClient.chain,
  isConnected: false,

  isRightNetwork: false,

  connect: () => {},
  disconnect: () => {},

  gsnSigner: undefined,

  safeSwitchChain: async () => {},
});

export const useWeb3Context = () => {
  return useContext(web3ProviderContext);
};

export const Web3ContextProvider = (props: PropsWithChildren) => {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <Web3ContextProviderContent>
          {props.children}
        </Web3ContextProviderContent>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

const Web3ContextProviderContent = (props: PropsWithChildren) => {
  const connectManager = useConnect();
  const [isInitializedGsnProvider, setIsInitializedGsnProvider] = useState(false);
  const account = useAccount();
  const { disconnect } = useDisconnect();

  const connect = useCallback(
    async (connector: Connector) => {
      connectManager.connect({ connector });
    },
    [connectManager],
  );

  const publicClient = useClient({ config });

  const { data: walletClient } = useConnectorClient({ config });

  const client = useMemo(() => {
    if (!account.chain) {
      return publicClient as Client<Transport, Chain>;
    }

    return walletClient as Client<Transport, Chain, Account>;
  }, [account.chain, publicClient, walletClient]);

  const gsnRelayProvider = useMemo(() => {
    if (!client?.chain?.id) return undefined;

    const gsnConfig = gsnConfigMap[chainIdToNetworkMap[Number(client.chain.id)]];

    if (!gsnConfig || !client) return undefined;

    if (!client.account) return undefined;

    const jsonRpcSigner = clientToSigner(client as Client<Transport, Chain, Account>);
    const web3ProviderBase = gsnRawProvider(jsonRpcSigner);
    const relayProvider = RelayProvider.newProvider({
      provider: web3ProviderBase as unknown as RelayProvider,
      config: {
        loggerConfiguration: { logLevel: 'error' },
        paymasterAddress: gsnConfig.paymaster,
        maxRelayNonceGap: 10000,
        preferredRelays: gsnConfig.relays,
      },
    });

    return relayProvider;
  }, [client]);

  const gsnSigner = useMemo((): providers.JsonRpcSigner | undefined => {
    if (!gsnRelayProvider || !isInitializedGsnProvider) return undefined;
    const gsnProvider = new providers.Web3Provider(
      gsnRelayProvider as unknown as providers.ExternalProvider,
    );

    return gsnProvider.getSigner();
  }, [gsnRelayProvider, isInitializedGsnProvider]);

  useEffect(() => {
    setIsInitializedGsnProvider(false);
    if (gsnRelayProvider && !gsnRelayProvider.connected) {
      ;(async () => {
        await gsnRelayProvider.init();
        setIsInitializedGsnProvider(true);
      })();
    }
  }, [gsnRelayProvider]);

  const provider = useMemo(() => {
    if (!client) return;
    return clientToProvider(client);
  }, [client]);

  const signer = useMemo(() => {
    if (!client?.account) return;
    return clientToSigner(client as Client<Transport, Chain, Account>);
  }, [client]);

  const isRightNetwork = useMemo((): boolean => {
    return (
      Boolean((!account?.chain && provider && !account?.address)) ||
      Boolean(account?.chain?.id && chainIdToNetworkMap[account.chain.id])
    );
  }, [account, provider]);

  const safeSwitchChain = useCallback(async (id: number) => {
    await switchChain(config, { chainId: id });
  }, []);

  if (!provider) {
    return (
      <Wrap>
        <motion.div
          className="breathing-q"
          animate={{ scale: 1.3 }}
          transition={{
            repeat: Infinity,
            repeatType: 'reverse',
            ease: 'easeOut',
            duration: 0.75
          }}
        >
          <img
            className="breathing-q__logo"
            src="/logo.png"
            alt="q"
          />
        </motion.div>
      </Wrap>
    );
  }

  return (
    <web3ProviderContext.Provider
      value={{
        connectManager,

        client: client,

        address: account.address ?? '',
        chain: client?.chain,
        isConnected: account.isConnected,

        isRightNetwork,

        gsnSigner,

        connect,
        disconnect,

        safeSwitchChain,
        provider,
        signer
      }}
    >
      {props.children}
    </web3ProviderContext.Provider>
  );
};

export const getCurrentClient = async (): Promise<Client<Transport, Chain, Account>> => {
  const account = getAccount(config);

  if (!account?.address) {
    return getClient(config) as Client<Transport, Chain, Account>;
  }

  const connectorClient = await getConnectorClient(config, {
    account: account.address,
    chainId: account.chainId ?? account.chain?.id,
  }) as Client<Transport, Chain, Account>;

  return connectorClient;
};

export const getNetworkConfig = async () => {
  const currentClient = await getCurrentClient();

  const networkName = currentClient.chain?.id && chainIdToNetworkMap?.[currentClient.chain.id];

  return networkConfigsMap[networkName || ORIGIN_NETWORK_NAME];
};
